import React, { useMemo, useState } from "react";
import Modal from "./Modal";
import { formatItalianNumber } from "../utils/formatItalianNumber";

const euro = (value) => `${formatItalianNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

export const ImportFileTotals = ({ rows, fileName, onClose }) => {
  const [tipologieTotaliModal, setTipologieTotaliModal] = useState(new Set(["Z", "I", "B"]));
  const totaliPerPrestazione = useMemo(() => {
    const result = new Map();
    rows.filter((row) => !["esclusa", "eliminata"].includes(row.stato)
      && tipologieTotaliModal.has(row.tipologia_fatturazione)).forEach((row) => {
      const codice = String(row.codice_ago || row.nuovo_codice_ago || row.prestazione || "—").replace(/^0+(?=\d)/, "");
      const current = result.get(codice) || {
        codice,
        descrizione: row.descrizione_prestazione || row.descrizione_fattura || "—",
        quantita: 0,
        importoFisso: 0,
        totale: 0,
        tipologie: new Set(),
      };
      current.quantita += codice === "1421" ? Math.min(Number(row.quantita || 0), 500) : Number(row.quantita || 0);
      current.importoFisso += Number(row.importo_fisso || 0);
      current.totale += Number(row.totale || 0);
      current.tipologie.add(row.tipologia_fatturazione);
      result.set(codice, current);
    });
    return [...result.values()].sort((a, b) => a.codice.localeCompare(b.codice, "it", { numeric: true }));
  }, [rows, tipologieTotaliModal]);
  const totalePrestazioniModal = useMemo(() => totaliPerPrestazione.reduce((sum, item) => sum + item.totale, 0), [totaliPerPrestazione]);
  return (
        <Modal isOpen size="compact" closeButton={onClose}
          headerText={<div className="text-center"><div>Totali per codice prestazione</div><div className="mt-0.5 text-xs font-normal text-gray-500">Import file · {fileName}</div></div>}>
          <div className="overflow-hidden rounded-md border border-slate-200">
            <div className="grid gap-3 border-b border-slate-200 bg-slate-50 px-4 py-3 sm:grid-cols-[1fr_auto] sm:items-center">
              <div>
                <div className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">Mostra tipologia</div>
                <div className="flex gap-2">
                {[["Z", "bg-blue-600 text-white border-blue-600 shadow-sm"], ["I", "bg-emerald-600 text-white border-emerald-600 shadow-sm"], ["B", "bg-purple-600 text-white border-purple-600 shadow-sm"]].map(([id, activeClass]) => (
                  <button key={id} type="button" onClick={() => setTipologieTotaliModal((prev) => {
                    const next = new Set(prev);
                    next.has(id) ? next.delete(id) : next.add(id);
                    return next.size ? next : prev;
                  })}
                    title={id === "Z" ? "Zucchetti" : id === "I" ? "iiPay" : "Bpoint"}
                    className={`min-w-[42px] rounded-lg border px-3 py-2 text-sm font-semibold transition ${tipologieTotaliModal.has(id) ? activeClass : "border-slate-200 bg-white text-slate-400 hover:border-slate-300"}`}>
                    {id}
                  </button>
                ))}
                </div>
              </div>
              <div className="rounded-xl border border-blue-100 bg-white px-5 py-3 text-right shadow-sm">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Totale selezionato</div>
                <div className="mt-0.5 text-2xl font-bold text-blue-700">{euro(totalePrestazioniModal)}</div>
              </div>
            </div>
            <div className="max-h-[58vh] overflow-auto p-4">
              <table className="w-full border-collapse text-sm">
                <thead className="sticky top-0 z-10 bg-white text-xs uppercase text-gray-500">
                  <tr className="border-b">
                    <th className="px-3 py-2 text-left">Codice</th>
                    <th className="px-3 py-2 text-left">Descrizione</th>
                    <th className="px-3 py-2 text-right">Quantità</th>
                    <th className="px-3 py-2 text-right">Importo fisso</th>
                    <th className="px-3 py-2 text-right">Totale</th>
                  </tr>
                </thead>
                <tbody>
                  {totaliPerPrestazione.map((item) => {
                    const isZ = item.tipologie.has("Z");
                    const isI = item.tipologie.has("I");
                    const isB = item.tipologie.has("B");
                    const rowClass = isB
                      ? "border-l-purple-500 bg-purple-50/70 hover:bg-purple-100/70"
                      : isZ && isI
                      ? "border-l-blue-500 bg-gradient-to-r from-blue-50/90 to-emerald-50/90 hover:from-blue-100/80 hover:to-emerald-100/80"
                      : isI
                        ? "border-l-emerald-500 bg-emerald-50/70 hover:bg-emerald-100/70"
                        : "border-l-blue-500 bg-blue-50/70 hover:bg-blue-100/70";
                    return (
                    <tr key={item.codice} className={`border-b border-l-4 border-gray-100 transition ${rowClass}`}>
                      <td className={`px-3 py-2 font-semibold ${isB ? "text-purple-700" : isI && !isZ ? "text-emerald-700" : "text-blue-700"}`}>{item.codice}</td>
                      <td className="px-3 py-2 text-gray-700">{item.descrizione}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{formatItalianNumber(item.quantita, { maximumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2 text-right text-gray-700">{item.importoFisso ? euro(item.importoFisso) : "—"}</td>
                      <td className="px-3 py-2 text-right font-semibold text-gray-900">{euro(item.totale)}</td>
                    </tr>
                    );
                  })}
                  {!totaliPerPrestazione.length && <tr><td colSpan="5" className="px-3 py-8 text-center text-gray-400">Nessuna prestazione nel periodo</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </Modal>
  );
};
